// Project context skeleton and the "pin to context" insertion. Pure text helpers shared by the api
// (project creation, pins), the db seed (personal spaces) and the web (heading dropdown).

/** The skeleton headings every project context starts with; the pin dropdown offers exactly these. */
export const CONTEXT_HEADINGS = ['Goal', 'Constraints', 'Decisions', 'Open questions', 'Memory'] as const;
export type ContextHeading = (typeof CONTEXT_HEADINGS)[number];

export const PERSONAL_CONTEXT_LINE =
  'This is a private space: only its owner can see it. Use it for thinking that has no project yet.';

export interface ContextSkeletonInput {
  name: string;
  /** Rendered as the paragraph under the title (a team project's description). */
  description?: string | null;
  /** Adds the private-space line under the title. */
  personal?: boolean;
}

/** "# Name", an optional intro paragraph, then the skeleton headings with an empty body each. */
export function renderContextSkeleton(input: ContextSkeletonInput): string {
  const intro: string[] = [];
  if (input.personal) intro.push(PERSONAL_CONTEXT_LINE);
  const description = input.description?.trim();
  if (description) intro.push(description);
  const head = [`# ${input.name.trim()}`, ...intro].join('\n\n');
  const sections = CONTEXT_HEADINGS.map((h) => `## ${h}\n`).join('\n');
  return `${head}\n\n${sections}`;
}

/** One line: whitespace runs collapsed, trimmed, cut to `max` characters. */
export function oneLine(text: string, max = 500): string {
  const line = text.replace(/\s+/g, ' ').trim();
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

/** The first sentence of a text (up to . ! or ? followed by whitespace), or the whole text as one line. */
export function firstSentence(text: string, max = 500): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  const m = /^(.*?[.!?])(?:\s|$)/.exec(flat);
  return oneLine(m?.[1] ?? flat, max);
}

interface HeadingLine {
  index: number;
  level: number;
  title: string;
}

function findHeadings(lines: string[]): HeadingLine[] {
  const out: HeadingLine[] = [];
  lines.forEach((line, index) => {
    const m = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (m) out.push({ index, level: m[1]!.length, title: m[2]!.trim() });
  });
  return out;
}

/**
 * Inserts "- text" as the last bullet of the section under `heading` (any level, case-insensitive match on the
 * title). The section runs to the next heading of the same or a higher level. A missing heading is appended as
 * "## heading" at the end. Blank-line hygiene: one blank line between the bullets and the next heading.
 */
export function insertUnderHeading(content: string, heading: string, text: string): string {
  const bullet = `- ${oneLine(text)}`;
  const normalized = content.replace(/\r\n/g, '\n');
  const lines = normalized.length === 0 ? [] : normalized.split('\n');
  const headings = findHeadings(lines);
  const target = headings.find((h) => h.title.toLowerCase() === heading.trim().toLowerCase());
  if (!target) {
    const body = normalized.replace(/\s+$/, '');
    return `${body.length > 0 ? `${body}\n\n` : ''}## ${heading.trim()}\n\n${bullet}\n`;
  }
  const next = headings.find((h) => h.index > target.index && h.level <= target.level);
  const sectionEnd = next ? next.index : lines.length;
  // Last non-blank line of the section (the heading itself when the section is empty).
  let last = target.index;
  for (let i = sectionEnd - 1; i > target.index; i -= 1) {
    if (lines[i]!.trim().length > 0) {
      last = i;
      break;
    }
  }
  const insert = last === target.index ? ['', bullet] : [bullet];
  const before = lines.slice(0, last + 1);
  let remainder = lines.slice(last + 1);
  while (remainder.length > 0 && remainder[0]!.trim().length === 0) remainder = remainder.slice(1);
  // One blank line before whatever follows, or a trailing newline at the end of the file.
  const rest = remainder.length === 0 ? [''] : ['', ...remainder];
  return [...before, ...insert, ...rest].join('\n');
}
